/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/anchor-has-content */
import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import { Typography, Box, TextField, Button, Autocomplete, FormHelperText } from '@mui/material';
import { ChevronDownIcon, CrossButtonIcons, DuplicateIcon } from '../../icons';
import GenerateLinkModal from './GenerateLinkModal';
import QrCodeModal from './QrCodeModal';
import { buildCampaignLink, DEFAULT_FORM, getFormPayload, getSourceOptionList, isApprovedSource, normalizeSourceValue } from '../../containers/campaign-container/utils';

export default function CreateCampaignModal({ onClose, open, isEdit, data, onSubmit, sourceOptions = [] }) {
    const [formData, setFormData] = React.useState(DEFAULT_FORM);
    const [errors, setErrors] = React.useState({});
    const [loading, setLoading] = React.useState(false);
    const [copiedIndex, setCopiedIndex] = React.useState(null);
    const [isGenerateLinkModal, setIsGenerateLinkModal] = React.useState(false);
    const [generatedData, setGeneratedData] = React.useState(null);
    const [isShort, setIsShort] = React.useState(false);
    const [isQrCodeOpen, setIsQrCodeOpen] = React.useState(false);
    React.useEffect(() => {
        if (open) {
            setFormData(data ? getFormPayload(data) : DEFAULT_FORM);
            setErrors({});
            setGeneratedData(data || null);
            setIsShort(false);
        }
    }, [open, data]);

    const sourceLookup = React.useMemo(() => {
        return getSourceOptionList(sourceOptions.map((item) => item?.value || item?.key || item?.label)).map((item) => ({
            ...item,
            value: item?.value || item?.key || item?.label || '',
            label: item?.label || item?.value || item?.key || '',
        }));
    }, [sourceOptions]);

    const selectedSource = sourceLookup.find((item) => item.value === normalizeSourceValue(formData.campaignSource)) || null;
    const sourceValue = selectedSource || null;

    const handleClickGenerateModal = () => {
        setIsGenerateLinkModal(true)
    }
    const handleCloseGenerateModal = () => {
        setIsGenerateLinkModal(false)
    }

    const handleCopy = (text, index) => {
        if (!text || text === '-') return;
        navigator.clipboard.writeText(text);

        setCopiedIndex(index);

        setTimeout(() => {
            setCopiedIndex(null);
        }, 1500);
    };

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const link = React.useMemo(() => {
        return buildCampaignLink({
            destinationURL: generatedData?.destinationURL || formData.destinationURL,
            campaignSource: formData.campaignSource,
            campaignName: formData.campaignName,
        });
    }, [generatedData, formData.destinationURL, formData.campaignSource, formData.campaignName]);

    const getShortLink = (url) => {
        if (!url || url === '-') return '-';
        if (url.length <= 40) return url;
        return url.slice(0, 40) + '...';
    };

    const handleSaveCampaign = async () => {
        try {
            if (!onSubmit) {
                onClose();
                return;
            }

            setLoading(true);
            const response = await onSubmit(formData, data);
            setLoading(false);

            if (!response?.success) {
                setErrors({
                    ...(response?.errors || {}),
                    form: response?.message || '',
                });
                return;
            }

            const nextData = response?.data || { ...formData };
            setGeneratedData(nextData);

            if (isEdit) {
                onClose();
            } else {
                onClose();
                handleClickGenerateModal();
            }
        } catch (error) {
            setLoading(false);
            setErrors(error?.data?.errors || {});
        } finally {
            setLoading(false);
        }
    };

    return (
        <React.Fragment>

            <Dialog
                open={open}
                onClose={loading ? undefined : onClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                sx={{
                    '& .MuiPaper-root': {
                        padding: '20px 14px',
                        width: '800px',
                        borderRadius: '7px'
                    }
                }}
            >
                <Box>
                    <Box sx={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px' }}>
                        <Box>
                            <Typography sx={{ color: '#101828', fontWeight: '500', fontSize: '22px', fontFamily: 'Neue Montreal' }}>
                                {isEdit ? "Edit Campaign" : "Create Campaign"}
                            </Typography>
                            <Typography sx={{ color: '#4A5565', fontWeight: '400', fontSize: '14px', fontFamily: 'Neue Montreal' }}>

                                {isEdit ? "Edit and manage your campaign tracking links" : "Create and manage your campaign tracking links"}
                            </Typography>
                        </Box>

                        <Box sx={{
                            background: '#D9D9D9', width: '35px', height: '35px', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '8px'
                        }}>
                            <CrossButtonIcons onClick={loading ? undefined : onClose} style={{ width: '16px' }} />
                        </Box>

                    </Box>

                    <Box>
                        <Box sx={{ marginBottom: '10px' }}>
                            <Typography sx={{ fontFamily: 'Neue Montreal', color: '#000', fontSize: '15px', fontWeight: '500', marginBottom: '5px' }}>Destination URL <span style={{ color: '#EF4444' }}>*</span></Typography>
                            <TextField
                                // disabled={true}
                                // readonly={true}
                                value={formData.destinationURL}
                                onChange={(event) => handleFieldChange('destinationURL', event.target.value)}
                                placeholder='Enter destination url'
                                sx={{
                                    width: '100%', '& .MuiInputBase-formControl': {
                                        height: '40px', borderRadius: '7px', fontSize: '14px', marginBottom: '5px', fontFamily: 'Neue Montreal'
                                    }, fontFamily: 'Neue Montreal'
                                }}
                            />
                            {errors.destinationURL ? <FormHelperText error>{errors.destinationURL}</FormHelperText> : null}
                        </Box>
                        <Box sx={{ marginBottom: '10px' }}>
                            <Typography sx={{ fontFamily: 'Neue Montreal', color: '#000', fontSize: '15px', fontWeight: '500', marginBottom: '5px' }}>Campaign Source  <span style={{ color: '#EF4444' }}>*</span></Typography>
                            <Autocomplete
                                value={sourceValue}
                                onChange={(e, newValue) => {
                                    handleFieldChange('campaignSource', newValue?.value || newValue?.label || '');
                                }}
                                options={sourceLookup}
                                popupIcon={<ChevronDownIcon />}
                                getOptionLabel={(option) => {
                                    return option.label || option.value || '';
                                }}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: "offset",
                                                options: {
                                                    offset: [0, 5],
                                                },
                                            },
                                        ],
                                    },
                                    paper: {
                                        sx: {
                                            backgroundColor: "#fff",
                                            borderRadius: "10px",
                                            boxShadow: "0px 0px 4px 0px #00000040",

                                        }
                                    }
                                }}
                                renderOption={(props, option) => (
                                    <li {...props} style={{ padding: '6px 12px' }}>
                                        <span style={{ fontSize: '14px', fontWeight: 400, color: '#000' }}>
                                            {option.label}
                                        </span>
                                    </li>
                                )}

                                sx={{
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '7px',
                                        backgroundColor: '#fff',
                                    },

                                }}

                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Campaign source"
                                        error={Boolean(errors.campaignSource) || Boolean(isEdit && formData.campaignSource && !isApprovedSource(formData.campaignSource))}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: '40px',
                                                fontSize: '14px',
                                                fontFamily: 'Neue Montreal'
                                            },
                                            width: '100%',

                                        }}
                                    />
                                )}

                                PaperProps={{
                                    sx: {
                                        mt: 1.5,
                                        borderRadius: "12px",
                                        boxShadow: "0px 0px 4px 0px #00000040",
                                        border: "1px solid #DFE5EB",
                                    }
                                }}
                            />
                            {errors.campaignSource ? <FormHelperText error>{errors.campaignSource}</FormHelperText> : isEdit && formData.campaignSource && !isApprovedSource(formData.campaignSource) ? <FormHelperText error>Select a valid campaign source before saving</FormHelperText> : null}
                        </Box>
                        <Box sx={{ marginBottom: '10px' }}>
                            <Typography sx={{ fontFamily: 'Neue Montreal', color: '#000', fontSize: '15px', fontWeight: '500', marginBottom: '5px' }}>Campaign Name   <span style={{ color: '#EF4444' }}>*</span></Typography>
                            <TextField
                                value={formData.campaignName}
                                onChange={(event) => handleFieldChange('campaignName', event.target.value)}
                                placeholder='Enter campaign name'
                                sx={{
                                    width: '100%', '& .MuiInputBase-formControl': {
                                        height: '40px', borderRadius: '7px', fontSize: '14px', marginBottom: '5px', fontFamily: 'Neue Montreal'
                                    }, fontFamily: 'Neue Montreal'
                                }}
                            />
                            {errors.campaignName ? <FormHelperText error>{errors.campaignName}</FormHelperText> : null}
                        </Box>
                        <Box sx={{ marginBottom: '10px' }}>
                            <Typography sx={{ fontFamily: 'Neue Montreal', color: '#000', fontSize: '15px', fontWeight: '500', marginBottom: '5px' }}>Remarks  (optional)
                            </Typography>

                            <Box sx={{ background: '#fff', border: '1px solid #ddd', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                <textarea
                                    value={formData.remarks}
                                    onChange={(event) => handleFieldChange('remarks', event.target.value)}
                                    placeholder='Add notes about this campaign...'
                                    style={{ background: 'none', border: 'none', fontSize: '13px', fontWeight: '400', width: '100%', height: '80px' }}
                                ></textarea>
                            </Box>
                        </Box>


                        <Box sx={{ marginBottom: '10px' }}>
                            {
                                isEdit && (
                                    <>
                                        <Box>
                                            <Typography sx={{ fontFamily: 'Neue Montreal', color: '#000', fontSize: '15px', fontWeight: '500', marginBottom: '5px' }}>
                                                Generated UTM Link
                                            </Typography>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <Box
                                                    sx={{
                                                        border: '1px dashed #2E80DB',
                                                        padding: '7px',
                                                        borderRadius: '7px',
                                                        background: '#F8FAFC',
                                                        width: '55%',
                                                    }}
                                                >
                                                    <a
                                                        href={link !== "-" ? link : undefined}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: '#2E80DB',
                                                            textDecoration: 'underline',
                                                            fontSize: '14px',
                                                            fontWeight: '400',
                                                            display: 'block',
                                                            width: '100%',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {isShort ? getShortLink(link) : link}
                                                    </a>
                                                </Box>

                                                <Box
                                                    onClick={() => handleCopy(link, 0)}
                                                    sx={{
                                                        border: '1px solid #ddd',
                                                        padding: '7px',
                                                        borderRadius: '7px',
                                                        cursor: 'pointer',
                                                        position: 'relative'
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'Neue Montreal',
                                                            fontSize: '14px',
                                                            fontWeight: '400',
                                                            color: '#3E3E3E'
                                                        }}
                                                    >
                                                        Copy <DuplicateIcon />
                                                    </Typography>

                                                    {copiedIndex === 0 && (
                                                        <span
                                                            style={{
                                                                position: 'absolute',
                                                                top: '-25px',
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                background: '#000',
                                                                color: '#fff',
                                                                padding: '3px 10px',
                                                                borderRadius: '20px',
                                                                fontSize: '10px'
                                                            }}
                                                        >
                                                            Copied
                                                        </span>
                                                    )}
                                                </Box>
                                                {/* <Box
                                                    onClick={() => setIsShort(prev => !prev)}
                                                    sx={{
                                                        border: '1px solid #ddd', padding: '7px', borderRadius: '7px',
                                                        cursor: 'pointer',
                                                        background: isShort ? '#F0F9FD' : 'transparent',
                                                        '&:hover': { background: '#f5f5f5' }
                                                    }}
                                                >
                                                    <Typography sx={{ fontFamily: 'Neue Montreal', fontSize: '14px', fontWeight: '400', color: '#3E3E3E' }}>
                                                        {isShort ? 'Full Link' : 'Shorten'}
                                                    </Typography>
                                                </Box> */}
                                                <Box
                                                    onClick={() => {
                                                        setIsQrCodeOpen(true);
                                                        onClose();
                                                    }}
                                                    sx={{
                                                        border: '1px solid #ddd', padding: '7px', borderRadius: '7px',
                                                        cursor: 'pointer',
                                                        '&:hover': { background: '#f5f5f5' }
                                                    }}
                                                >
                                                    <Typography sx={{ fontFamily: 'Neue Montreal', fontSize: '14px', fontWeight: '400', color: '#3E3E3E' }}>
                                                        QR Code
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </>
                                )
                            }
                        </Box>

                        <Box sx={{ marginTop: '16px' }}>
                            {errors.form ? <FormHelperText error sx={{ marginBottom: '8px' }}>{errors.form}</FormHelperText> : null}
                            <Button
                                onClick={handleSaveCampaign}
                                disabled={loading}
                                sx={{
                                    width: '100%', padding: '10px 12px', textAlign: 'center', background: '#BE1F1F', color: '#fff', fontWeight: '400', textTransform: 'capitalize', fontFamily: 'Neue Montreal', fontSize: '15px',  '&:hover': { background: '#9B1A1A' }
                                }}>
                                {loading ? 'Saving...' : 'Save Campaign'}
                            </Button>
                        </Box>

                    </Box>

                </Box>
            </Dialog>

            <GenerateLinkModal open={isGenerateLinkModal} onClose={handleCloseGenerateModal} data={generatedData} />
            <QrCodeModal open={isQrCodeOpen} onClose={() => setIsQrCodeOpen(false)} link={link} campaignName={formData.campaignName} />
        </React.Fragment>
    );
}
